import { motion } from 'framer-motion';
import type { ConversationMessage } from '@/store/conversation';

interface SJChatMessageProps {
  message: ConversationMessage;
}

export function SJChatMessage({ message }: SJChatMessageProps) {
  const isUser = message.role === 'user';

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
      className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}
    >
      <div
        className={`max-w-[85%] rounded-[16px] px-4 py-3 md:max-w-[70%] ${
          isUser
            ? 'bg-[var(--sj-text-primary)] text-[var(--sj-background)]'
            : 'bg-[var(--sj-surface-muted)] text-[var(--sj-text-primary)]'
        }`}
      >
        {isUser ? (
          <p className="text-sm leading-relaxed whitespace-pre-wrap">{message.content}</p>
        ) : (
          <div className="sj-chat-response text-sm leading-relaxed prose prose-sm max-w-none dark:prose-invert">
            <MarkdownContent content={message.content} />
          </div>
        )}
      </div>
    </motion.div>
  );
}

function MarkdownContent({ content }: { content: string }) {
  const html = parseMarkdown(content);
  return <div dangerouslySetInnerHTML={{ __html: html }} />;
}

function parseMarkdown(text: string): string {
  let html = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  // Headings
  html = html.replace(/^### (.+)$/gm, '<h4 class="text-sm font-semibold mt-3 mb-1">$1</h4>');
  html = html.replace(/^## (.+)$/gm, '<h3 class="text-sm font-semibold mt-3 mb-1">$1</h3>');

  // Bold
  html = html.replace(/\*\*\[([^\]]+)\]\(([^)]+)\)\*\*/g, '<a href="$2" class="font-semibold text-primary-600 hover:text-primary-700 dark:text-primary-400 underline decoration-primary-300">$1</a>');
  html = html.replace(/\*\*([^*]+)\*\*/g, '<strong class="font-semibold">$1</strong>');

  // Italic
  html = html.replace(/\*([^*]+)\*/g, '<em>$1</em>');

  // Links
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-primary-600 hover:text-primary-700 dark:text-primary-400 underline decoration-primary-300">$1</a>');

  // Unordered lists
  html = html.replace(/^- (.+)$/gm, '<li class="ml-4 list-disc">$1</li>');
  html = html.replace(/((?:<li[^>]*>.*<\/li>\n?)+)/g, '<ul class="my-2 space-y-1">$1</ul>');

  // Numbered lists
  html = html.replace(/^\d+\. (.+)$/gm, '<li class="ml-4 list-decimal">$1</li>');

  // Paragraphs
  html = html.replace(/\n\n/g, '</p><p class="my-2">');
  html = `<p class="my-1">${html}</p>`;

  // Clean empty paragraphs
  html = html.replace(/<p class="my-[12]"><\/p>/g, '');
  html = html.replace(/<p class="my-1">(<h[34])/g, '$1');
  html = html.replace(/(<\/h[34]>)<\/p>/g, '$1');
  html = html.replace(/<p class="my-1">(<ul)/g, '$1');
  html = html.replace(/(<\/ul>)<\/p>/g, '$1');

  return html;
}
